import React from "react";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {cn} from "@/lib/utils";
import {Localizer} from "@/main/react/Types";
import {createReactMount} from "@/main/react/mountContract";
import {getShellStateSnapshot, subscribeToShellState} from "@/main/navigation/ShellStateStore";
import {
    CREATE_DISCUSSION_ACTION,
    type FloatingActionDescriptor,
    type FloatingActionGroup,
    type FloatingActionLook,
    type FloatingActionState,
    isFloatingActionVisible,
    resolveFloatingActionsForPath,
    runFloatingActionBehavior,
} from "@/main/react/floating-actions/FloatingActionConfig";
import {
    getFloatingActionContributionsSnapshot,
    resolveActiveContributedGroup,
    subscribeToFloatingActionContributions,
} from "@/main/react/floating-actions/FloatingActionContributionStore";
import {useIsMobileViewport} from "@/main/react/hooks/useIsMobileViewport";
import {useCurrentPathname} from "@/main/react/hooks/useCurrentPathname";
import {useMobileBottomNavTucked, useMobileBottomNavVisible} from "@/main/react/header/MobileBottomNav";

const REGISTRY_GROUP_SCOPE = "app-registry";

/*
 * The pathname this component renders against comes from `@/main/react/hooks/useCurrentPathname`.
 *
 * <p>It used to be a PRIVATE copy declared here: a `useState` mirror listening to the
 * navigation-intent event, `popstate` and `pageshow`, with a trailing rAF re-read. Devo-gate finding
 * **G1** (2026-08-27) is what that copy cost. `361a003432` fixed the shared hook by having
 * `NavigationHistoryState` announce every history write, which fixed the header's compose control and
 * left this one broken, because the private copy heard none of the four signals: an anchor click is
 * handled by `EnhancedNavigation.handleDocumentClick`, which calls `runEnhancedNavigation` directly
 * and never dispatches the intent, a push is not a traversal so there is no `popstate`, no document
 * loads so there is no `pageshow`, and the rAF re-read only ran from inside those handlers. The
 * pathname stayed on the composer, `isComposerPath` kept matching, and the corner kept offering
 * *Start another draft* where the member expected *Create*.
 *
 * <p>Folded rather than taught the fourth signal, per rule 31: the remedy for one value living in two
 * places is to delete a copy, never to add another sync. Guarded by `FloatingActionButtons.spec.tsx`.
 * Triage: designs/08-audits-and-process/2026-08-27-devo-gate-triage-361a003432.md
 */


/** Filled-pill treatment for a `separate` (app-FAB look) action. */
function separateButtonClasses(variant: FloatingActionLook["variant"], state: FloatingActionState): string {
    const base = (() => {
        switch (variant) {
            case "secondary":
                return "bg-secondary text-secondary-foreground shadow-[var(--surface-shadow-md)]";
            case "neutral":
                return "bg-card text-foreground border border-border shadow-[var(--surface-shadow-md)]";
            case "primary":
            default:
                return "bg-primary text-primary-foreground shadow-[var(--surface-shadow-primary)]";
        }
    })();
    return cn(base, "hover:shadow-[var(--surface-shadow-lg)] active:scale-95", state.active && "ring-2 ring-ring/60");
}

/** Quiet ghost treatment for a button living inside a `grouped` enclosing pill. */
function groupedButtonClasses(state: FloatingActionState): string {
    return cn(
        "text-foreground/80 standard-hover active:scale-95",
        state.active && "bg-foreground/15 text-foreground",
    );
}

function sizeClasses(size: FloatingActionLook["size"]): {button: string; icon: string} {
    if (size === "md") {
        return {button: "h-12 w-12", icon: "h-5 w-5"};
    }
    return {button: "h-14 w-14", icon: "h-6 w-6"};
}

function FloatingActionButton({
    action,
    localizer,
    loggedIn,
    grouped,
}: {
    action: FloatingActionDescriptor;
    localizer: Localizer;
    loggedIn: boolean;
    grouped: boolean;
}) {
    const Icon = action.look.icon;
    const label = localizer(action.look.labelKey);
    const size = sizeClasses(action.look.size);
    const state = action.state ?? {};
    const inactive = !!state.disabled || !!state.loading;

    const button = (
        <button
            type="button"
            aria-label={label}
            aria-pressed={state.active ? true : undefined}
            aria-busy={state.loading ? true : undefined}
            disabled={inactive}
            data-playwright-id={action.dataPlaywrightId}
            onClick={() => runFloatingActionBehavior(action, {localizer, loggedIn})}
            className={cn(
                "pointer-events-auto inline-flex items-center justify-center rounded-full",
                "touch-manipulation transition-[box-shadow,transform,background-color] duration-150 ease-out",
                "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/70 focus-visible:border-ring",
                "disabled:pointer-events-none disabled:opacity-50",
                size.button,
                grouped ? groupedButtonClasses(state) : separateButtonClasses(action.look.variant, state),
            )}
        >
            <Icon className={cn("stroke-current stroke-2 fill-none", size.icon)} aria-hidden="true" />
        </button>
    );

    // renderWrapper (e.g. a popover) owns its own affordance; skip the tooltip to avoid nested triggers.
    if (action.renderWrapper) {
        return <>{action.renderWrapper(button)}</>;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="left" sideOffset={8}>
                {label}
            </TooltipContent>
        </Tooltip>
    );
}

function FloatingActionGroupView({
    group,
    localizer,
    loggedIn,
}: {
    group: FloatingActionGroup;
    localizer: Localizer;
    loggedIn: boolean;
}) {
    if (group.render) {
        return <>{group.render()}</>;
    }

    const actions = group.actions ?? [];
    if (actions.length === 0) {
        return null;
    }

    const buttons = actions.map((action) => (
        <FloatingActionButton
            key={action.id}
            action={action}
            localizer={localizer}
            loggedIn={loggedIn}
            grouped={group.container === "grouped"}
        />
    ));

    if (group.container === "grouped") {
        return (
            <div className="pointer-events-auto relative flex flex-col gap-1 overflow-hidden rounded-full border border-border bg-background1/30 p-0 shadow-lg backdrop-blur-sm">
                {buttons}
            </div>
        );
    }

    return <>{buttons}</>;
}

/**
 * The group with the Create action removed while the phone bottom bar shows, or `null` when that leaves
 * nothing to draw. Returns the SAME group object when nothing changes, so the render below is stable
 * for every group that never carried Create (a page's "back to controls", a group page's "add post").
 */
export function withoutCreateWhileBottomNavShows(
    group: FloatingActionGroup | null,
    mobileBottomNavVisible: boolean,
): FloatingActionGroup | null {
    if (!group || !mobileBottomNavVisible || !group.actions) {
        // A `render` escape-hatch group (the editor cluster) is left alone: those pages hide the bar.
        return group;
    }
    const actions = group.actions.filter((action) => action.id !== CREATE_DISCUSSION_ACTION.id);
    if (actions.length === group.actions.length) {
        return group;
    }
    return actions.length > 0 || group.render ? {...group, actions} : null;
}

/**
 * A58 (`DISCUSSION-98`): the attribute `DiscussionMobileActionBar` writes on `<html>` while it is docked
 * to the bottom edge. Spelled here too rather than imported (this module is eager, that one is not);
 * `FloatingActionButtons.spec.tsx` (A58) pins the two spellings equal.
 */
export const DISCUSSION_ACTION_BAR_DOCKED_DOCUMENT_ATTRIBUTE = "data-discussion-action-bar-docked";

function readDiscussionActionBarDocked(): boolean {
    return typeof document !== "undefined"
        && document.documentElement.getAttribute(DISCUSSION_ACTION_BAR_DOCKED_DOCUMENT_ATTRIBUTE) === "true";
}

function subscribeToDiscussionActionBarDocking(listener: () => void): () => void {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
        return () => {};
    }
    const observer = new MutationObserver(listener);
    observer.observe(document.documentElement, {attributes: true, attributeFilter: [DISCUSSION_ACTION_BAR_DOCKED_DOCUMENT_ATTRIBUTE]});
    return () => observer.disconnect();
}

export function FloatingActionButtons({
    loggedIn: initialLoggedIn = false,
    localizer,
}: {
    loggedIn?: boolean;
    localizer: Localizer;
}) {
    const pathname = useCurrentPathname();
    const isMobileViewport = useIsMobileViewport();
    const shellStateSnapshot = React.useSyncExternalStore(
        subscribeToShellState,
        getShellStateSnapshot,
        getShellStateSnapshot,
    );
    const contributedGroups = React.useSyncExternalStore(
        subscribeToFloatingActionContributions,
        getFloatingActionContributionsSnapshot,
        getFloatingActionContributionsSnapshot,
    );
    const loggedIn = shellStateSnapshot.mainHeader?.loggedIn ?? initialLoggedIn;
    // The phone bottom bar (`MobileBottomNav`) owns Create on a phone: while it SHOWS, this corner
    // yields the Create action to its Create tab and floats the rest above the bar. Read from the one
    // attribute the bar writes rather than re-deriving its route and keyboard rules here, so the two
    // can never disagree (gate `848db59a7e`, 2026-09-14: the bar sat over the FAB and every phone tap
    // on Create landed on the Notifications tab).
    const mobileBottomNavVisible = useMobileBottomNavVisible();
    // MAF-19 / U41 (r156): the corner follows the bar down and up, so it never floats over the gap the bar left.
    const mobileBottomNavTucked = useMobileBottomNavTucked();
    // A58 (`DISCUSSION-98`): the docked discussion bar owns the corner the same way, so Create yields to it
    // instead of sitting half behind it. A boolean snapshot, so no identity churn (rule 31).
    const discussionActionBarDocked = React.useSyncExternalStore(
        subscribeToDiscussionActionBarDocking,
        readDiscussionActionBarDocked,
        () => false,
    );

    // A live page contribution always owns the corner; otherwise the static registry action set does.
    const contributedGroup = resolveActiveContributedGroup(contributedGroups);
    const resolvedGroup: FloatingActionGroup | null = contributedGroup
        ? contributedGroup
        : (() => {
              const actions = resolveFloatingActionsForPath(pathname).filter((action) =>
                  isFloatingActionVisible(action, {loggedIn, isMobileViewport}),
              );
              return actions.length > 0
                  ? {scopeId: REGISTRY_GROUP_SCOPE, container: "separate", actions}
                  : null;
          })();
    const activeGroup = withoutCreateWhileBottomNavShows(resolvedGroup, mobileBottomNavVisible || discussionActionBarDocked);

    if (!activeGroup) {
        return null;
    }

    const bottomInsetPx = activeGroup.bottomInsetPx ?? 0;
    const bottomNavReserve = mobileBottomNavVisible ? " + var(--mobile-bottom-nav-height)" : "";

    return (
        <div
            className="pointer-events-none fixed right-2 z-layer-sticky flex flex-col items-end gap-3 sm:right-5 transition-transform duration-(--motion-duration-sheet) ease-(--motion-ease-standard) motion-reduce:transition-none"
            style={{
                bottom: `calc(1rem + env(safe-area-inset-bottom) + ${bottomInsetPx}px${bottomNavReserve})`,
                // `transform`, never `bottom`, so the follow is composited (U33) and the reserve above stays the source of truth.
                transform: mobileBottomNavVisible && mobileBottomNavTucked ? "translateY(var(--mobile-bottom-nav-height))" : undefined,
            }}
            data-floating-actions-root
            data-follows-bottom-nav-tuck={mobileBottomNavVisible && mobileBottomNavTucked ? "true" : "false"}
            data-layout-monitor-ignore="true"
        >
            <FloatingActionGroupView group={activeGroup} localizer={localizer} loggedIn={loggedIn} />
        </div>
    );
}

export const mountFloatingActions = createReactMount<{loggedIn: boolean}>({
    name: "FloatingActions",
    getProps: ({dataset}) => ({
        loggedIn: dataset.loggedIn === "true",
    }),
    render: (props, {localizer}) => (
        <FloatingActionButtons loggedIn={props.loggedIn} localizer={localizer} />
    ),
});
